import _ from "lodash";
import React, { useEffect, useState, useImperativeHandle } from "react";
import { usePrevious } from "Hooks";

// DS
import {
  Button,
  Dropdown,
  DropdownMenu,
  IconButton,
  Tooltip,
} from "@toddle-edu/ds-web";
import { LanguageOutlined } from "@toddle-edu/ds-icons";

// Modules
import { updateAIConversation } from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import {
  getUserId,
  getCurrentCurriculumProgramType,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";

// Utils
import { preferredLanguageFieldOptions } from "../../modules/Utils";
import { getSettingValue } from "Utils/ConfigurationUtils";

// third party
import { connect } from "react-redux";

// Constants

// Scss
import classes from "./AILanguageSelection.scss";

const AILanguageSelection = props => {
  const {
    isDisabled = false,
    customRef,
    configurationValue,
    selectedLanguage,
    userId,
    updateSettings,
    conversationId,
    testId,
    updateAIConversation,
    preferredLanguageOptions,
    conversationMetadata = {},
    buttonSize = "small",
    mode,
    buttonProps = {},
    showLanguageLabel = true,
    showIconButton = false,
  } = props;

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [language, setLanguage] = useState("");

  const previousSelectedLanguage = usePrevious(selectedLanguage);

  const languageLabel = _.find(preferredLanguageOptions, {
    key: language,
  })?.label;

  // set previous selected language of user as preferred voice language
  useEffect(() => {
    if (
      !_.isEmpty(selectedLanguage) &&
      !_.isEqual(previousSelectedLanguage, selectedLanguage) &&
      (_.isEmpty(language) || !_.isEqual(language, selectedLanguage))
    ) {
      setLanguage(selectedLanguage);

      // if previous selection does not exist then set default value (platform language) as preferred voice language
      if (_.isEmpty(configurationValue)) {
        onUpdateSetting(selectedLanguage);
      }
    }
  }, [language, selectedLanguage, configurationValue]);

  useEffect(() => {
    const updateConversationMetadata = () => {
      const { label, lng: code } =
        _.find(preferredLanguageOptions, {
          key: language,
        }) || {};

      if (conversationId && label && code) {
        updateAIConversation({
          input: {
            id: conversationId,
            metadata: {
              ...(conversationMetadata || {}),
              language: label,
              languageCode: code,
              selectedDropdownLanguage: language,
            },
          },
        });
      }
    };

    const { selectedDropdownLanguage } = conversationMetadata || {};

    if (
      language &&
      (mode === "create" ||
        (mode === "edit" &&
          (!selectedDropdownLanguage || selectedDropdownLanguage !== language)))
    ) {
      updateConversationMetadata();
    }
  }, [language]);

  useImperativeHandle(
    customRef,
    () => ({
      preferredVoiceObj: { language },
      preferredLanguageOptions,
    }),
    [language, preferredLanguageOptions]
  );

  const onUpdateSetting = async value => {
    try {
      if (value) {
        const settings = [
          {
            name: "ConfigureAssessmentAIVoiceLanguage",
            value,
          },
        ];

        await updateSettings({
          id: userId,
          type: "user",
          settings,
          showToastMsg: false,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // this will make dropdown menu to open outside of modal
  const getPopupContainer = () => document.body;

  const onChangeLanguage = e => {
    const { key, domEvent } = e;
    domEvent?.stopPropagation(); // persist context bar state

    setLanguage(key);
    onUpdateSetting(key);
    setIsDropdownVisible(false);
  };

  return (
    <Dropdown
      overlay={
        <DropdownMenu
          value={language}
          options={preferredLanguageOptions}
          onClick={onChangeLanguage}
          isSearchEnabled={true}
          showSelection={true}
          selectionType={"tick"}
          testId={`${testId}-languageSelectionDropdown`}
        />
      }
      onVisibleChange={setIsDropdownVisible}
      getPopupContainer={getPopupContainer}
      disabled={isDisabled}
      placement={"bottomRight"}
      overlayClassName={classes.dropdownOverlay}
    >
      <Tooltip
        tooltip={languageLabel}
        showArrow={true}
        visible={!isDropdownVisible}
      >
        {showIconButton ? (
          <IconButton
            icon={<LanguageOutlined />}
            type={"plain"}
            variant={"neutral"}
            size={buttonSize}
            disabled={isDisabled}
            isActivated={isDropdownVisible}
            dsVersion={"2.0"}
            testId={`${testId}-languageSelection`}
            {...buttonProps}
          />
        ) : (
          <Button
            icon={<LanguageOutlined />}
            type={"plain"}
            variant={"neutral"}
            size={buttonSize}
            disabled={isDisabled}
            isActivated={isDropdownVisible}
            dsVersion={"2.0"}
            testId={`${testId}-languageSelection`}
            {...buttonProps}
          >
            {showLanguageLabel ? (
              <span className={classes.languageLabel}>{languageLabel}</span>
            ) : null}
          </Button>
        )}
      </Tooltip>
    </Dropdown>
  );
};

AILanguageSelection.displayName = "AILanguageSelection";

const mapStateToProps = (ownProps, state) => {
  const { t, i18n } = ownProps;

  const userId = getUserId(state);
  const currentCurriculumProgramType = getCurrentCurriculumProgramType(state);
  const currentCurriculumProgramId = getSelectedCurriculumId(state);

  const configurationName = "ConfigureAssessmentAIVoiceLanguage";
  let configurationValue = null;

  // if setting value contains "" then getSettingValue can throw error
  try {
    configurationValue = _.toString(
      getSettingValue({
        name: configurationName,
        readFromUserAndOrgLevel: true,
      })
    );
  } catch (err) {
    console.error(err);
  }

  const preferredLanguageOptions = preferredLanguageFieldOptions({
    t,
    showAllGlobalVoiceLanguages: true,
  });

  let selectedLanguage = configurationValue;

  // if configurationValue is empty, set default value to true which will set platform language as AI voice language
  if (_.isEmpty(configurationValue)) {
    const languageId = _.find(preferredLanguageOptions, language => {
      return _.get(language, "lng") === _.get(i18n, "language", "en");
    })?.key;

    if (languageId) selectedLanguage = _.toString(languageId);
  }

  return {
    userId,
    currentCurriculumProgramType,
    currentCurriculumProgramId,
    configurationName,
    selectedLanguage,
    configurationValue,
    preferredLanguageOptions,
  };
};

const mapActionCreators = {
  updateSettings,
  updateAIConversation,
};

export default connect(mapStateToProps, mapActionCreators)(AILanguageSelection);
